'use client';

import type { CSSProperties, ReactNode } from 'react';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';

/**
 * Magazine-style micro-label. Used above every section heading and inside
 * hero compositions. ~9px mono uppercase with wide tracking — the single
 * most replicable luxury typographic move.
 *
 *   <Eyebrow>Spring Series · 2026</Eyebrow>
 */
export function Eyebrow({
  children,
  size = 'md',
  tone = 'muted',
  align = 'start',
  style,
}: {
  children: ReactNode;
  /** sm = 8px, md = 9px, lg = 10px */
  size?: 'sm' | 'md' | 'lg';
  /** muted = on-bg secondary; foreground = full ink; inverse = white-on-image */
  tone?: 'muted' | 'foreground' | 'inverse';
  align?: 'start' | 'center' | 'end';
  style?: CSSProperties;
}) {
  const t = useNativeTokens();
  const sizePx = size === 'sm' ? 8 : size === 'lg' ? 10 : 9;
  const tracking = size === 'sm' ? '0.18em' : size === 'lg' ? '0.24em' : '0.22em';
  const color =
    tone === 'inverse'
      ? 'rgba(255,255,255,0.78)'
      : tone === 'foreground'
        ? t.foreground
        : t.muted;

  return (
    <span
      style={{
        display: 'block',
        fontFamily: t.fontMono,
        fontSize: sizePx,
        letterSpacing: tracking,
        textTransform: 'uppercase',
        color,
        textAlign: align,
        ...style,
      }}
    >
      {children}
    </span>
  );
}
